// Software card on the admin device page (Overview tab): every app's installed
// version and the version on offer, from the Scout's latest update check, set
// against its last heartbeat so a Scout that has stopped checking for updates
// stands out. Logic and its reasoning live in utils/deviceSoftware.ts.
//
// Read-only. Data: GET /api/admin/neptune/updates?device_id=X&limit=5 (newest
// first; the few extra rows let a failed check fall back to the versions from
// the check before it), and GET /api/admin/ota/live/manifests for the latest
// release on each channel, and GET /api/admin/ota/live/release-dates for when
// each version first reached a Scout. Without the manifests the card falls back
// to comparing against what the Scout was offered at its last check; without
// the dates it simply shows none.

import { useQuery } from '@tanstack/react-query';
import { format, formatDistanceToNow, formatDistanceToNowStrict } from 'date-fns';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { api } from '../../services/api';
import {
  appVersions,
  checkReceivedAt,
  isCheckOld,
  isScoutTablet,
  latestRelease,
  latestWithVersions,
  parseTimestamp,
  releaseStatus,
  releasedAt,
  softwareHeadline,
  updateChannel,
  updateCheckHealth,
  type AppVersion,
  type AppVersionState,
  type LatestRelease,
  type ReleaseDate,
  type ReleaseStatus,
  type UpdateCheckRow,
} from '../../utils/deviceSoftware';

interface DeviceSoftwareCardProps {
  device: {
    id: string;
    device_type?: string | null;
    serial_number?: string | null;
    last_seen_at?: string | null;
  };
}

// `then` is the wording when the check is old (or the Scout has stopped
// checking): the offer it compared against has likely moved on since.
const STATE_PILL: Record<AppVersionState, { now: string; then: string; className: string }> = {
  current: { now: 'Up to date', then: 'Was up to date', className: 'bg-green-500/20 text-green-400' },
  update: { now: 'Update waiting', then: 'Update was waiting', className: 'bg-amber-500/20 text-amber-300' },
  unknown: { now: 'Unknown', then: 'Unknown', className: 'bg-slate-500/20 text-slate-300' },
};

// Against the live release: true today, so never worded in the past.
const RELEASE_PILL: Record<ReleaseStatus, { label: string; title: string; className: string }> = {
  current: { label: 'Up to date', title: 'Installed version is the latest release', className: 'bg-green-500/20 text-green-400' },
  waiting: { label: 'Update waiting', title: 'Offered at its last update check, not installed yet', className: 'bg-amber-500/20 text-amber-300' },
  'not-offered': { label: 'Not yet offered', title: 'Released after its last update check', className: 'bg-orange-500/20 text-orange-300' },
};

interface AppRow extends AppVersion {
  latest: LatestRelease | null;
  release: ReleaseStatus | null;
  /** When each version was first offered to any Scout; null when unknown. */
  installedAt: Date | null;
  latestAt: Date | null;
  offeredAt: Date | null;
}

function fullDate(d: Date): string {
  return format(d, 'd MMM yyyy, HH:mm');
}

function dayDate(d: Date): string {
  return format(d, 'd MMM yyyy');
}

function ago(d: Date): string {
  return formatDistanceToNow(d, { addSuffix: true });
}

function Version({ name, code }: { name: string | null; code: number | null }) {
  if (!name) return <span className="text-slate-500">—</span>;
  return (
    <span className="font-mono text-white">
      {name}
      {code != null && <span className="ml-1.5 text-slate-500">({code})</span>}
    </span>
  );
}

/** "Released 3 Aug 2026 (2 months ago)", under a version. */
function Released({ at }: { at: Date | null }) {
  if (!at) return null;
  return (
    <span className="block whitespace-normal text-[13px] text-slate-500 lg:mt-0.5" title={`First offered to a Scout ${fullDate(at)}`}>
      {/* Each half stays whole, so a narrow column breaks between them. */}
      <span className="whitespace-nowrap">Released {dayDate(at)}</span>{' '}
      <span className="whitespace-nowrap">({formatDistanceToNowStrict(at, { addSuffix: true })})</span>
    </span>
  );
}

function Warning({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div role="alert" className="flex gap-3 rounded-lg border border-amber-700/50 bg-amber-900/20 p-3">
      <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-amber-400" aria-hidden="true" />
      <div>
        <p className="text-sm font-medium text-amber-200">{title}</p>
        <p className="mt-0.5 text-sm text-amber-100/80">{children}</p>
      </div>
    </div>
  );
}

// A table from lg up, where the content area beside the admin sidebar is wide
// enough for four columns. Narrower, each app reflows into a small block (name
// and status, then installed, then latest or offered) so nothing scrolls sideways.
const CELL = 'p-0 lg:px-3 lg:py-2';

// The explicit roles keep table semantics for screen readers below lg, where
// the display changes would otherwise drop them (Safari does).
function AppTable({ apps, live, past }: { apps: AppRow[]; live: boolean; past: boolean }) {
  const third = live ? 'Latest' : 'Offered';
  return (
    <div className="overflow-x-auto rounded-lg border border-slate-700">
      <table role="table" className="block w-full text-sm lg:table">
        <thead role="rowgroup" className="hidden bg-slate-900/40 text-left text-slate-400 lg:table-header-group">
          <tr role="row">
            <th role="columnheader" scope="col" className="px-3 py-2 font-medium">App</th>
            <th role="columnheader" scope="col" className="px-3 py-2 font-medium">Installed</th>
            <th role="columnheader" scope="col" className="px-3 py-2 font-medium">{third}</th>
            <th role="columnheader" scope="col" className="px-3 py-2 font-medium">Status</th>
          </tr>
        </thead>
        <tbody role="rowgroup" className="block divide-y divide-slate-700/50 lg:table-row-group">
          {apps.map((a) => {
            const release = live && a.release ? RELEASE_PILL[a.release] : null;
            const fallback = STATE_PILL[a.state];
            const pill = release
              ? { label: release.label, title: release.title, className: release.className }
              : { label: past ? fallback.then : fallback.now, title: undefined, className: fallback.className };
            // What the Scout was offered at its last check, when that adds something:
            // not today's latest, and not simply the version it already had.
            const showOffered = live && a.offered != null && a.offeredCode !== a.latest?.code && a.offeredCode !== a.installedCode;
            return (
              <tr
                key={a.pkg}
                role="row"
                className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1 px-3 py-2.5 lg:table-row lg:p-0"
              >
                <td role="cell" className={`order-1 text-slate-200 lg:whitespace-nowrap ${CELL}`} title={a.pkg}>
                  {/* Wraps the hint under the name on a narrow screen, with no indent. */}
                  <span className="inline-flex flex-wrap items-baseline gap-x-2">
                    <span>{a.name}</span>
                    {a.hint && <span className="text-[13px] text-slate-500">{a.hint}</span>}
                  </span>
                </td>
                <td role="cell" className={`order-3 col-span-2 lg:whitespace-nowrap ${CELL}`}>
                  <span className="inline-block w-20 text-slate-400 lg:hidden">Installed</span>
                  <Version name={a.installed} code={a.installedCode} />
                  <Released at={a.installedAt} />
                </td>
                <td role="cell" className={`order-4 col-span-2 lg:whitespace-nowrap ${CELL}`}>
                  <span className="inline-block w-20 text-slate-400 lg:hidden">{third}</span>
                  {live
                    ? <Version name={a.latest?.version ?? null} code={a.latest?.code ?? null} />
                    : <Version name={a.offered} code={a.offeredCode} />}
                  {/* Same version as installed: its date is already shown there. */}
                  {live
                    ? a.latest?.code !== a.installedCode && <Released at={a.latestAt} />
                    : a.offeredCode !== a.installedCode && <Released at={a.offeredAt} />}
                  {showOffered && (
                    <span className="block whitespace-normal text-[13px] text-slate-500 lg:mt-0.5">
                      <span className="whitespace-nowrap">Offered {a.offered}{a.offeredCode != null ? ` (${a.offeredCode})` : ''}</span>{' '}
                      <span className="whitespace-nowrap">at its last check</span>
                    </span>
                  )}
                </td>
                <td role="cell" className={`order-2 justify-self-end whitespace-nowrap ${CELL}`}>
                  {/* The column header is hidden below lg; this names the pill there. */}
                  <span className="sr-only lg:hidden">Status:</span>
                  <span title={pill.title} className={`inline-flex rounded-md px-2 py-0.5 text-[13px] font-medium ${pill.className}`}>{pill.label}</span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function DeviceSoftwareCard({ device }: DeviceSoftwareCardProps) {
  const isScout = isScoutTablet(device);

  // Same cadence as the telemetry poll on this page: a new check shows up
  // within a minute of the Scout sending it.
  const { data, isLoading, isError } = useQuery({
    queryKey: ['device-software', device.id],
    queryFn: () => api.adminGetNeptuneUpdates({ device_id: device.id, limit: 5 }),
    enabled: isScout,
    refetchInterval: 60_000,
  });

  // The latest release per channel, shared by every device page.
  const { data: live } = useQuery({
    queryKey: ['ota-live-manifests'],
    queryFn: () => api.adminOtaLiveManifests(),
    enabled: isScout,
    refetchInterval: 60_000,
  });

  // Release dates change only when something is released; shared and slow.
  const { data: releaseDates } = useQuery({
    queryKey: ['ota-release-dates'],
    queryFn: () => api.adminOtaReleaseDates(),
    enabled: isScout,
    staleTime: 5 * 60_000,
    refetchInterval: 5 * 60_000,
  });

  if (!isScout) return null;

  const rows = (Array.isArray(data?.updates) ? data.updates : []) as UpdateCheckRow[];
  const latest = rows[0] ?? null;
  const versionsFrom = latestWithVersions(rows);
  const latestFailed = latest?.result === 'check_failed';
  const channel = updateChannel(versionsFrom?.deviceMeta ?? latest?.deviceMeta);
  const apps: AppRow[] = appVersions(versionsFrom?.appDetails).map((a) => {
    const rel = latestRelease(live?.manifests, channel, a.pkg);
    const dates = releaseDates as ReleaseDate[] | undefined;
    return {
      ...a,
      latest: rel,
      release: releaseStatus(a, rel),
      installedAt: releasedAt(dates, a.pkg, a.installedCode, a.installed),
      latestAt: rel ? releasedAt(dates, a.pkg, rel.code, rel.version) : null,
      offeredAt: releasedAt(dates, a.pkg, a.offeredCode, a.offered),
    };
  });
  // Compare against today's release whenever the manifests list any of these apps.
  const isLive = apps.some((a) => a.latest != null);
  const anyDates = apps.some((a) => a.installedAt || (isLive ? a.latestAt : a.offeredAt));

  const checkedAt = checkReceivedAt(latest);
  const versionsAt = checkReceivedAt(versionsFrom);
  const seenAt = parseTimestamp(device.last_seen_at);
  const health = updateCheckHealth(device.last_seen_at, checkedAt);
  // Without the live release, statuses describe the offer at the time of the
  // versions' check; once that is old, or the Scout has stopped checking, they
  // are worded in the past and the header makes no claim about today.
  const past = !isLive && (health.kind === 'silent' || isCheckOld(versionsAt));
  const headline = isLive
    ? softwareHeadline(
        apps.map((a) => ({ ...a, state: a.release === 'current' ? 'current' : a.release ? 'update' : 'unknown' })),
        latestFailed,
      )
    : past ? null : softwareHeadline(apps, latestFailed);
  const waitingLabel = (n: number) =>
    isLive ? `${n} app${n === 1 ? '' : 's'} out of date` : n === 1 ? '1 update waiting' : `${n} updates waiting`;
  const errorMessage = typeof latest?.errorMessage === 'string' && latest.errorMessage ? latest.errorMessage : null;

  return (
    <div className="rounded-lg border border-slate-700 bg-slate-800/50 p-5" data-testid="device-software">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-200">Software</h3>
        {headline?.kind === 'waiting' && (
          <span className="rounded-md bg-amber-500/20 px-2 py-0.5 text-[13px] font-medium text-amber-300">
            {waitingLabel(headline.count)}
          </span>
        )}
        {headline?.kind === 'current' && (
          <span className="rounded-md bg-green-500/20 px-2 py-0.5 text-[13px] font-medium text-green-400">
            All up to date
          </span>
        )}
      </div>

      {isLoading && <p className="text-sm text-slate-400">Loading software versions…</p>}
      {/* A failed background refresh keeps the last good data on screen. */}
      {isError && !data && <p className="text-sm text-slate-400">Software versions are not available right now.</p>}

      {data && (
        <div className="space-y-4">
          {(checkedAt || seenAt) && (
            <p className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-400">
              {checkedAt && (
                <span title={fullDate(checkedAt)}>
                  Last update check <span className="text-slate-200">{ago(checkedAt)}</span>
                </span>
              )}
              {seenAt && (
                <span title={fullDate(seenAt)}>
                  Last heartbeat <span className="text-slate-200">{ago(seenAt)}</span>
                </span>
              )}
              {versionsFrom && (
                <span title="Which release channel the update server gives this Scout, from how its launcher is signed">
                  Channel <span className="text-slate-200">{channel === 'platform' ? 'Platform' : channel === 'stable' ? 'Stable' : 'Stable (assumed)'}</span>
                </span>
              )}
            </p>
          )}

          {health.kind === 'never-connected' && (
            <p className="text-sm text-slate-400">
              This Scout has not connected yet, so it has no versions to show.
            </p>
          )}

          {health.kind === 'never-checked' && (
            <Warning title="No update checks received">
              This Scout has been online (last heartbeat {dayDate(health.lastSeenAt)}) but has never sent an
              update check, so its app versions are unknown and it may not be getting updates.
            </Warning>
          )}

          {health.kind === 'silent' && (
            <Warning title="Not checking for updates">
              Its last update check was {dayDate(health.lastCheckAt)}, but it kept sending heartbeats for{' '}
              {health.days} days after that. The versions below are from that check, and the Scout may not be
              getting updates.
            </Warning>
          )}

          {latestFailed && (
            <p className="rounded-lg border border-red-800/50 bg-red-900/20 px-3 py-2 text-sm text-red-300">
              The last update check failed{errorMessage ? `: ${errorMessage}` : '.'}
            </p>
          )}

          {apps.length > 0 && (
            <>
              <AppTable apps={apps} live={isLive} past={past} />
              <p className="text-[13px] text-slate-500">
                {versionsFrom !== latest && versionsAt && `Installed versions from the check on ${fullDate(versionsAt)}. `}
                {isLive
                  ? 'Latest is what the update server offers this Scout’s channel now.'
                  : 'Offered is what the update server offered the Scout at that check.'}
                {anyDates && ' Release dates are when the update server first offered a version to any Scout.'}
              </p>
            </>
          )}

          {latest && apps.length === 0 && !latestFailed && (
            <p className="text-sm text-slate-400">The last update check listed no apps.</p>
          )}
        </div>
      )}
    </div>
  );
}

export default DeviceSoftwareCard;
